import React, { ReactElement, useCallback, useMemo, useState } from 'react';
import { Modal, Typography } from '@vtblife/uikit';

import Slider from '@search/vtbeco-frontend-core/view/common/components/Slider';
import {
    SpecialEvent,
    SpecialEvents
} from '@search/whitelabel-search-www/client/common/components/NewBuildingCard/NewBuildingCardSpecialEvents';
import {
    SpecialEventIconName,
} from '@search/whitelabel-search-www/client/common/components/SvgSpriteIcon';
import { CoreSvgSpriteIcon } from '@search/vtbeco-frontend-core/view/common/components/CoreSvgSpriteIcon/CoreSvgSpriteIcon';

import upperFirst from 'lodash/upperFirst';
import camelCase from 'lodash/camelCase';
import classname from '@search/classname/src';
import CustomTypography from '../../../../domain/newbuilding/components/common/CustomTypography';

import './style.css';

const cn = classname.bind(null, 'NewBuildingSpecialEvents');

const DEFAULT_COLOR = '#77738C';
const COLORS = {
    Promo: '#28B9BE',
    Discount: '#E91F0C',
    InstallmentPlan: '#9173FA',
    SalesStart: '#3A86F7',
    Gift: '#FF71CF',
    TradeIn: DEFAULT_COLOR,
    Arbitrary: '#28B9BE',
};

const NBSelectedSpecialEventModal = ({
    selectedEvent,
    onClose: handleClose,
    newBuildingTitle,
    developerTitle,
    callBtn
}: {
    selectedEvent: SpecialEvent;
    onClose: () => void;
    newBuildingTitle: string;
    developerTitle?: string;
    callBtn: ReactElement;
}) => {
    return (
        <Modal
            opened
            size='s'
            onClose={handleClose}
        >
            <NBSelectedSpecialEvent
                selectedEvent={selectedEvent}
                newBuildingTitle={newBuildingTitle}
                developerTitle={developerTitle}
            />
            <div className={cn('callBtn')}>
                {callBtn}
            </div>
        </Modal>
    );
};

export const NewBuildingSpecialEvents = ({
    specialEvents,
    newBuildingTitle,
    developerTitle,
    callBtn,
    isMobile = false
}: {
    specialEvents: SpecialEvents;
    newBuildingTitle: string;
    developerTitle?: string;
    callBtn: ReactElement;
    isMobile?: boolean;
}) => {
    const [ selectedEvent, selectEvent ] = useState<SpecialEvent | null>(null);
    const [ maxItems, setMaxItems ] = useState<number>(4);

    const closeModal = useCallback(() => selectEvent(null), []);
    const handleSetMaxItems = useCallback((value: number) => setMaxItems(value), []);

    const eventsTotal = useMemo(() => specialEvents.length, [ specialEvents ]);

    return (
        <div className={cn('', { isMobile })}>
            {isMobile ? (
                <>
                    {
                        specialEvents.map((specialEvent, i) => (
                            <div
                                key={i}
                                className={cn('ItemWrapper', { isMobile })}
                            >
                                <NBSpecialEvent
                                    specialEvent={specialEvent}
                                    onClick={() => selectEvent(specialEvent)}
                                />
                            </div>
                        ))
                    }
                </>
            ) : (
                <Slider
                    contentClassName={cn('SliderContent')}
                    onSetMaxItems={handleSetMaxItems}
                >
                    {
                        specialEvents.map((specialEvent, i) => (
                            <div
                                key={i}
                                className={cn('ItemWrapper')}
                                style={{
                                    minWidth: `${100 / Math.min(eventsTotal, maxItems)}%`
                                }}
                            >
                                <NBSpecialEvent
                                    specialEvent={specialEvent}
                                    onClick={() => selectEvent(specialEvent)}
                                />
                            </div>
                        ))
                    }
                </Slider>
            )}
            {selectedEvent && (
                <NBSelectedSpecialEventModal
                    selectedEvent={selectedEvent}
                    onClose={closeModal}
                    newBuildingTitle={newBuildingTitle}
                    developerTitle={developerTitle}
                    callBtn={callBtn}
                />)
            }
        </div>
    );
};

export const NBSpecialEvent = ({
    specialEvent,
    onClick: handleClick
}: {
    specialEvent: SpecialEvent;
    onClick: () => void;
}) => {
    const {
        isHighlighted,
        type,
        title,
        description: { short: description },
        duration: { short: duration }
    } = specialEvent;

    return (
        <div
            className={cn('Item', {
                highlighted: isHighlighted
            })}
            onClick={handleClick}
        >
            <CoreSvgSpriteIcon
                name={camelCase(type) as SpecialEventIconName}
                color={COLORS[upperFirst(camelCase(type)) as SpecialEventIconName]}
                style={{ fill: `${COLORS[upperFirst(camelCase(type)) as SpecialEventIconName]}` }}
            />
            <CoreSvgSpriteIcon
                name='QuestionMark'
                width={16}
                height={16}
            />
            <CustomTypography
                className={cn('ItemTitle')}
                level='primary-alone'
                bold
                dangerouslySetInnerHTML={{
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    __html: title
                }}
            />
            {
                description && (
                    <CustomTypography
                        className={cn('ItemDescription')}
                        level='secondary-alone'
                        color='secondary'
                        dangerouslySetInnerHTML={{
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            __html: description
                        }}
                    />
                )
            }
            {
                duration && (
                    <CustomTypography
                        className={cn('ItemDuration')}
                        level='secondary-alone'
                        color='secondary'
                        dangerouslySetInnerHTML={{
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            __html: duration
                        }}
                    />
                )
            }
        </div>
    );
};

const enforceFullStop = (sentence: string) => sentence.replace(/([^.])$/, '$1.');

export const NBSelectedSpecialEvent = ({
    isMobile = false,
    selectedEvent,
    newBuildingTitle,
    developerTitle,
}: {
    isMobile?: boolean;
    selectedEvent: SpecialEvent;
    newBuildingTitle: string;
    developerTitle?: string;
}) => {
    const {
        title,
        description: {
            short: shortDesc,
            full: fullDesc
        },
        duration: { full: duration },
    } = selectedEvent;

    const description = fullDesc ||
        shortDesc && enforceFullStop(shortDesc);

    const infoTexts = [
        enforceFullStop(upperFirst([
            duration,
        ].filter(Boolean).join(', '))),
    ].filter(Boolean) as string[];

    return (
        <div className={cn('SelectedItem', { mobile: isMobile })}>
            <CustomTypography
                level={isMobile ? 'primary' : 'h4'}
                bold
                dangerouslySetInnerHTML={{
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    __html: title
                }}
            />
            <div className={cn('SelectedItemInfo')}>
                {
                    description && (
                        <CustomTypography dangerouslySetInnerHTML={{
                            // eslint-disable-next-line @typescript-eslint/naming-convention
                            __html: description
                        }} />
                    )
                }
                {
                    infoTexts.map((text, i) => (
                        <CustomTypography
                            key={i}
                            color='secondary'
                            dangerouslySetInnerHTML={{
                                // eslint-disable-next-line @typescript-eslint/naming-convention
                                __html: text
                            }}
                        />
                    ))
                }
            </div>
            <Typography
                className={cn('NewBuildingTitle')}
                variant='h4'
            >
                {newBuildingTitle}
            </Typography>
            {
                developerTitle ? (
                    <Typography className={cn('DeveloperTitle')}>
                        {`Застройщик ${
                            developerTitle
                        }. Позвоните в отдел продаж и узнайте подробнее.`}
                    </Typography>
                ) : null
            }
        </div>
    );
};

export const NewBuildingSpecialEventsHeading = ({
    title,
    subtitle,
    seoTitle,
    isMobile = false
}:{
    title: string;
    subtitle: string;
    seoTitle: string | null;
    isMobile?: boolean;
}) => {
    return (
        <Typography
            variant={isMobile ? 'h3' : 'h2'}
            className={cn('title', { isMobile })}
            bold
        >
            {title}
            <Typography
                variant='primary-alone'
                color='secondary'
                className={cn('subtitle', { isMobile })}
            >
                <span>
                    {subtitle} {seoTitle}
                </span>
            </Typography>
        </Typography>
    );
};
